import { Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { StateService } from '@uirouter/angular';
import { Subject, debounceTime, distinctUntilChanged, of, switchMap, takeUntil, tap } from 'rxjs';
import { FeaturesService } from 'src/app/shared/services/features.service';
import { ProjectSuggestionDataService } from '../../data-access/widgets/project-suggestion-data.service';

@Component({
  selector: 'app-quick-find-project-widget',
  template: `
    <app-dashboard-card title="Quickly Locate a Project by Name or ID">
      <mat-form-field appearance="outline" style="width:100%; margin-bottom:8px;">
        <mat-label>Project name</mat-label>
        <input matInput [formControl]="projectNameControl" [matAutocomplete]="nameAuto" aria-label="Find project by name" (keydown.enter)="onNameEnter($event)" />
      </mat-form-field>
      <mat-autocomplete #nameAuto="matAutocomplete" [displayWith]="displayName" (optionSelected)="selectProject($event.option.value)">
        <mat-option *ngFor="let item of nameSuggestions; trackBy: trackByProjectOption" [value]="item">{{ renderName(item) }}</mat-option>
        <mat-option *ngIf="nameHasMore" disabled>More results are available with a more specific search</mat-option>
      </mat-autocomplete>

      <mat-form-field appearance="outline" class="project-id-field">
        <mat-label>Project ID</mat-label>
        <input matInput [formControl]="projectIdControl" [matAutocomplete]="idAuto" aria-label="Find project by ID" (keydown.enter)="onIdEnter($event)" />
      </mat-form-field>
      <mat-autocomplete #idAuto="matAutocomplete" [displayWith]="displayId" (optionSelected)="selectProject($event.option.value)">
        <mat-option *ngFor="let item of idSuggestions; trackBy: trackByProjectOption" [value]="item">{{ renderId(item) }}</mat-option>
        <mat-option *ngIf="idHasMore" disabled>More results are available with a more specific search</mat-option>
      </mat-autocomplete>
    </app-dashboard-card>
  `,
  styles: [
    `.project-id-field { width: 220px; max-width: 100%; }`,
    `@media (max-width: 768px) { .project-id-field { width: 100%; } }`
  ]
})
export class QuickFindProjectWidgetComponent implements OnDestroy {
  projectNameControl = new FormControl('');
  projectIdControl = new FormControl('');
  nameSuggestions: any[] = [];
  idSuggestions: any[] = [];
  nameHasMore = false;
  idHasMore = false;
  private readonly destroy$ = new Subject<void>();
  readonly displayName = (value: any) => this.renderName(value);
  readonly displayId = (value: any) => this.renderId(value);

  constructor(
    private readonly stateService: StateService,
    private readonly dataService: ProjectSuggestionDataService,
    private readonly featuresService: FeaturesService
  ) {
    this.projectNameControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      tap(value => {
        if (!value || String(value).length < 2) {
          this.nameSuggestions = [];
          this.nameHasMore = false;
        }
      }),
      switchMap(value => {
        const text = String(value || '');
        if (text.length < 2) {
          return of([]);
        }
        return this.dataService.findByName(text);
      })
    ).subscribe((items: any) => {
      const list = Array.isArray(items) ? items : [];
      this.nameHasMore = list.length > 10;
      this.nameSuggestions = list.slice(0, 10);
    });

    this.projectIdControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      tap(value => {
        if (!value || String(value).length < 2) {
          this.idSuggestions = [];
          this.idHasMore = false;
        }
      }),
      switchMap(value => {
        const text = String(value || '');
        if (text.length < 2) {
          return of([]);
        }
        return this.dataService.findById(text);
      })
    ).subscribe((items: any) => {
      const list = Array.isArray(items) ? items : [];
      this.idHasMore = list.length > 10;
      this.idSuggestions = list.slice(0, 10);
    });
  }

  renderName(item: any): string {
    const prefix = this.featuresService.enableLifecycleSecurity && item?.discriminator?.[0]
      ? `(${item.discriminator[0]}) `
      : '';
    return `${prefix}${item?.name || ''}`;
  }

  renderId(item: any): string {
    const prefix = this.featuresService.enableLifecycleSecurity && item?.discriminator?.[0]
      ? `(${item.discriminator[0]}) `
      : '';
    return `${prefix}${item?.projectID || item?.value || ''}`;
  }

  selectProject(item: any): void {
    if (!item) {
      return;
    }

    const projectId = String(item?.projectID || item?.value || '').trim();
    if (!projectId) {
      return;
    }

    const projectName = String(item?.projectName || item?.name || '').trim();

    this.stateService.go('search.project', {
      projectID: projectId,
      projectName: encodeURIComponent(projectName)
    });
  }

  onNameEnter(event: KeyboardEvent): void {
    this.trySelectExactMatch(event, this.projectNameControl.value, this.nameSuggestions, 'name');
  }

  onIdEnter(event: KeyboardEvent): void {
    this.trySelectExactMatch(event, this.projectIdControl.value, this.idSuggestions, 'id');
  }

  private trySelectExactMatch(event: KeyboardEvent, value: unknown, suggestions: any[], mode: 'name' | 'id'): void {
    const query = String(value || '').trim().toLowerCase();
    if (!query || !Array.isArray(suggestions) || suggestions.length === 0) {
      return;
    }

    const matches = suggestions.filter(item => {
      const candidate = mode === 'name'
        ? String(item?.name || '')
        : String(item?.projectID || item?.value || '');
      return candidate.trim().toLowerCase() === query;
    });

    if (matches.length === 1) {
      event.preventDefault();
      this.selectProject(matches[0]);
    }
  }

  trackByProjectOption(_: number, item: any): string {
    return String(item?.projectID || item?.name || item?.value || '');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
